import { type MatrixClient, SyncState } from "matrix-js-sdk/src/matrix";
import { EventEmitter } from "events";

import { MatrixClientPeg } from "../MatrixClientPeg";
import { type ActionPayload } from "../dispatcher/payloads";
import { type IDestroyable } from "../utils/IDestroyable";
import { Action } from "../dispatcher/actions";
import { type MatrixDispatcher } from "../dispatcher/dispatcher";

export abstract class ReadyWatchingStore extends EventEmitter implements IDestroyable {
    protected matrixClient?: MatrixClient;
    private dispatcherRef?: string;

    public constructor(protected readonly dispatcher: MatrixDispatcher) {
        super();
    }

    public async start(): Promise<void> {
        this.dispatcherRef = this.dispatcher.register(this.onAction);

        let clientPeg;
        try {
            clientPeg = MatrixClientPeg;
        } catch {
            // MatrixClientPeg can be undefined because of circular dependencies with other stores
        }
        const matrixClient = clientPeg?.get(); 
        if (matrixClient) {
            this.matrixClient = matrixClient;
            await this.onReady();
        }
    }

    public get mxClient(): MatrixClient | null {
        return this.matrixClient ?? null; // for external readonly access
    }

    public useUnitTestClient(cli: MatrixClient): void {
        this.matrixClient = cli;
    }

    public destroy(): void {
        this.dispatcher.unregister(this.dispatcherRef);
    }

    protected async onReady(): Promise<void> {
        // Default implementation is to do nothing.
    }

    protected async onNotReady(): Promise<void> {
        // Default implementation is to do nothing.
    }

    protected onDispatcherAction(payload: ActionPayload): void {
        // Default implementation is to do nothing.
    }

    private onAction = async (payload: ActionPayload): Promise<void> => {
        this.onDispatcherAction(payload);

        if (payload.action === "MatrixActions.sync") {
            // Only set the client on the transition into the PREPARED state.
            // Everything after this is unnecessary (we only need to know once we have a client)
            // and we intentionally don't set the client before this point to avoid stores
            // updating for every event emitted during the cached sync.
            if (
                payload.prevState !== SyncState.Prepared &&
                payload.state === SyncState.Prepared &&
                this.matrixClient !== payload.matrixClient
            ) {
                console.debug("[ReadyWatchingStore] Detected client ready, setting client and calling onReady");
                if (this.matrixClient) {
                    await this.onNotReady();
                }
                this.matrixClient = payload.matrixClient;
                await this.onReady();
            }
        } else if (payload.action === "on_client_not_viable" || payload.action === Action.OnLoggedOut) {
            console.debug(`[ReadyWatchingStore] Detected ${payload.action}, clearing client and resetting store`);
            if (this.matrixClient) {
                await this.onNotReady();
                this.matrixClient = undefined;
            }
        }
    };
}
